import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject, Subscription, forkJoin } from 'rxjs';
import { debounceTime, finalize, takeUntil } from 'rxjs/operators';

import { SnackBarService } from 'src/app/shared/components/snack-bar/snack-bar.service';

import { AppConstants } from '../../../../app.constants';
import { OrgSelectorService } from '../../../../core/services/org-selector.service';
import { OrganizationDto as BaseOrganizationDto } from '../../../../modules/organizations/dtos/organization';
import { OrganizationsService } from '../../../../modules/organizations/organizations.service';
import { ChipVariant } from '../../../../shared/components/chip/chip-variant.model';
import { Pagination } from '../../../../shared/components/pagination/models/pagination.model';
import { PaginationArgs } from '../../../../shared/components/pagination/models/pagination-args.model';
import { SortingType } from '../../../../shared/enums/sorting-type.enum';
import { MessageResponse } from '../../../../shared/models/message-response.model';
import { PartnerBroker } from '../../models/organizations.model';
import { OrganizationsLegacyService } from '../../organizations-legacy.service';

interface OrganizationBrokerLink {
	partnerBroker: PartnerBroker | null;
}

type OrganizationDto = BaseOrganizationDto & {
	hasDigitalHospital?: boolean;
	organizationBrokerLink?: OrganizationBrokerLink | null;
	organizationsPartners?: { partners: PartnerBroker }[];
	partnerBroker?: PartnerBroker | null;
	subsidiaries?: (BaseOrganizationDto & {
		organizationsPartners?: { partners: PartnerBroker }[];
		partnerBroker?: PartnerBroker | null;
	})[];
};

@Component({
	selector: 'app-organizations',
	styleUrls: ['./organizations.component.scss'],
	templateUrl: './organizations.component.html',
})
export class OrganizationsComponent implements OnInit, OnDestroy {
	brokerStats = { lives: 0, organizations: 0 };
	count = 0;
	currentPage = 1;
	filteredOrganizations: OrganizationDto[] = [];
	isCreateEnabled = false;
	isDisableFavoriteAction = false;
	isLoading = true;
	isSearchButtonDisabled = true;
	limit = 10;
	organizations: OrganizationDto[] = [];
	partnerBrokers: PartnerBroker[] = [];
	productFilterOptions = [{ value: 'all', viewValue: 'Todos' }];
	searchButton = true;
	searchForm: UntypedFormGroup;
	private readonly debounceTimeMs: number = AppConstants.debounce.time;
	private readonly productColorMap: { [key: string]: string } = {
		'gestao de saude': 'blue',
		'gestao operacional': 'green',
		'gestão de saúde': 'blue',
		'gestão operacional': 'green',
		seguro: 'orange',
		'seguro de vida': 'orange',
		vida: 'orange',
	};
	private readonly productTagVariants: ChipVariant[] = [
		'blue',
		'green',
		'orange',
	];
	private readonly sanusId: string = AppConstants.organization.id;
	private readonly searchOrganizationFilter = new Subject<string>();
	private searchSubscription$: Subscription = new Subscription();
	private toggleSubscription$: Subscription = new Subscription();

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly organizationsService: OrganizationsService,
		private readonly legacyOrganizationsService: OrganizationsLegacyService,
		private readonly organizationSelector: OrgSelectorService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly snackBarService: SnackBarService,
	) {
		this.searchForm = this.fb.group({
			brokerFilter: [[]],
			productFilter: ['all'],
			term: [null],
		});
	}

	calculateTotal(): void {
		this.organizations.forEach((organization) => {
			let totalBeneficiaries = organization?._count?.beneficiaries || 0;
			let totalUsers = organization?._count?.users || 0;

			if (organization.subsidiaries) {
				organization.subsidiaries.forEach((subsidiary) => {
					totalBeneficiaries += subsidiary?._count?.beneficiaries || 0;
					totalUsers += subsidiary?._count?.users || 0;
				});
			}
			organization._count.totalBeneficiaries = totalBeneficiaries;
			organization._count.totalUsers = totalUsers;
		});
	}

	clearAllFilters(): void {
		this.searchForm.patchValue({
			brokerFilter: [],
			productFilter: 'all',
			term: '',
		});
		this.currentPage = 1;
		this.searchOrganizations();
		this.updateQueryParams();
	}

	getActiveFilters(): { label: string; type: string; value: any }[] {
		const activeFilters: { label: string; type: string; value: any }[] = [];

		const productFilter = this.searchForm.get('productFilter')?.value;

		if (productFilter && productFilter !== 'all') {
			const productOption = this.productFilterOptions.find(
				(option) => option.value === productFilter,
			);

			if (productOption) {
				activeFilters.push({
					label: `Produto: ${productOption.viewValue}`,
					type: 'product',
					value: productFilter,
				});
			}
		}

		const brokerFilter = this.searchForm.get('brokerFilter')?.value;

		if (brokerFilter && brokerFilter.length > 0) {
			brokerFilter.forEach((brokerId: string) => {
				if (brokerId === 'none') {
					activeFilters.push({
						label: 'Sem Corretora',
						type: 'broker',
						value: 'none',
					});
				} else {
					const broker = this.partnerBrokers.find((b) => b.id === brokerId);

					if (broker) {
						activeFilters.push({
							label: broker.name_secondary,
							type: 'broker',
							value: brokerId,
						});
					}
				}
			});
		}

		const searchTerm = this.searchForm.get('term')?.value;

		if (searchTerm?.trim()) {
			activeFilters.push({
				label: `Busca: ${searchTerm}`,
				type: 'search',
				value: searchTerm,
			});
		}

		return activeFilters;
	}

	getProductTagClass(productName: string): string {
		const normalizedName = productName.toLowerCase().trim();

		for (const [key, color] of Object.entries(this.productColorMap)) {
			if (normalizedName.includes(key)) {
				return `chip-${color}`;
			}
		}

		let hash = 0;

		for (let i = 0; i < normalizedName.length; i++) {
			const char = normalizedName.charCodeAt(i);
			hash = (hash << 5) - hash + char;
			hash |= 0;
		}

		const variantIndex = Math.abs(hash) % this.productTagVariants.length;

		return `chip-${this.productTagVariants[variantIndex]}`;
	}

	loadDataAndInitialize(): void {
		const brokers$ = this.legacyOrganizationsService.getPartnerBrokers();
		const products$ = this.legacyOrganizationsService.getProducts();

		forkJoin({
			brokers: brokers$,
			products: products$,
		}).subscribe(({ brokers, products }) => {
			this.partnerBrokers = brokers;

			const options = products.map((p) => ({ value: p.id, viewValue: p.name }));
			this.productFilterOptions = [
				{ value: 'all', viewValue: 'Todos' },
				...options,
			];

			this.initializeFromQueryParams();
		});
	}

	loadMockBrokers(): void {
		this.partnerBrokers = [
			{ id: '1', name_secondary: 'Wiz' },
			{ id: '2', name_secondary: 'Sciath' },
			{ id: '3', name_secondary: 'Critéria' },
		] as PartnerBroker[];
	}

	loadRealBrokers(): void {
		this.legacyOrganizationsService.getPartnerBrokers().subscribe((brokers) => {
			this.partnerBrokers = brokers;
		});
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.searchSubscription$.unsubscribe();
		this.toggleSubscription$.unsubscribe();
	}

	ngOnInit(): void {
		const userOrganization = this.organizationSelector.getUserOrganization();

		if (userOrganization == this.sanusId) {
			this.isCreateEnabled = true;
		}

		this.loadDataAndInitialize();

		this.searchOrganizationFilter
			.pipe(debounceTime(this.debounceTimeMs), takeUntil(this.unsubscribe$))
			.subscribe(() => {
				this.searchOrganizations(true);
				this.updateQueryParams();
			});
		this.searchForm
			.get('productFilter')
			?.valueChanges.pipe(takeUntil(this.unsubscribe$))
			.subscribe(() => {
				this.currentPage = 1;
				this.searchOrganizations();
				this.updateQueryParams();
			});
		this.searchForm
			.get('brokerFilter')
			?.valueChanges.pipe(takeUntil(this.unsubscribe$))
			.subscribe(() => {
				this.currentPage = 1;
				this.searchOrganizations();
				this.updateQueryParams();
			});
	}

	onClearSearch(): void {
		this.searchForm.patchValue({ term: null });
		this.searchOrganizations();
		this.updateQueryParams();
		this.searchButton = true;
		this.isSearchButtonDisabled = true;
	}

	onEditGroup(id: string, name: string): void {
		localStorage.setItem('org-id', id);
		this.organizationSelector.setOrganizationId(id);
		this.organizationSelector.setOrganizationName(name);
		this.router.navigate(['organizacoes', id]);
	}

	onPageChange(event: PageEvent): void {
		this.currentPage = event.pageIndex + 1;
		this.limit = event.pageSize;
		this.searchOrganizations();
		this.updateQueryParams();
	}

	onSearch(): void {
		const term = this.searchForm.get('term')!.value;

		this.searchOrganizationFilter.next(term ?? '');
	}

	onSearchClick(): void {
		this.searchOrganizations();
		this.updateQueryParams();
		this.searchButton = false;
	}

	onSearchKeyUp(event: KeyboardEvent): void {
		const term = this.searchForm.get('term')!.value;

		if (event.key === 'Enter') {
			this.searchOrganizations();
			this.updateQueryParams();
			this.searchButton = false;

			return;
		}

		if (term?.length) {
			this.searchButton = true;
			this.isSearchButtonDisabled = false;
		} else {
			this.isSearchButtonDisabled = true;
		}
	}

	onToggleFavorite(id: string): void {
		this.isDisableFavoriteAction = true;
		this.toggleSubscription$ = this.legacyOrganizationsService
			.toggleFavoriteOrganization(id)
			.pipe(finalize(() => (this.isDisableFavoriteAction = false)))
			.subscribe(
				(response: MessageResponse) => {
					if (response?.type === 'success') {
						this.snackBarService.showSnack(
							response.message || 'Favorito atualizado com sucesso.',
							'success',
						);
						this.searchOrganizations();
					} else {
						this.snackBarService.showSnack(
							response.message || 'Erro ao atualizar favorito.',
							'error',
						);
					}
				},
				(error: any) => {
					const errorMessage =
						error.error?.message || 'Erro ao tentar atualizar favorito.';
					this.snackBarService.showSnack(errorMessage, 'error');
				},
			);
	}

	removeFilter(filter: { label: string; type: string; value: any }): void {
		switch (filter.type) {
			case 'product': {
				this.searchForm.get('productFilter')?.setValue('all');
				break;
			}

			case 'broker': {
				const currentBrokerFilter =
					this.searchForm.get('brokerFilter')?.value || [];
				const updatedBrokerFilter = currentBrokerFilter.filter(
					(value: string) => value !== filter.value,
				);
				this.searchForm.get('brokerFilter')?.setValue(updatedBrokerFilter);
				break;
			}

			case 'search': {
				this.searchForm.get('term')?.setValue('');
				break;
			}

			default: {
				break;
			}
		}
		this.currentPage = 1;
		this.searchOrganizations();
		this.updateQueryParams();
	}

	searchOrganizations(isFilter = false): void {
		if (!isFilter) {
			this.isLoading = true;
			this.organizations = [];
			this.filteredOrganizations = [];
		}

		const term = this.searchForm.get('term')?.value;

		if (term?.trim()) {
			this.searchWithSubsidiarySupport(term.trim());
		} else {
			this.searchWithoutTerm();
		}
	}

	trackByBroker(index: number, broker: PartnerBroker): string {
		return broker.id;
	}

	trackByFilter(
		index: number,
		filter: { label: string; type: string; value: any },
	): string {
		return `${filter.type}-${filter.value}`;
	}

	trackByOrganization(index: number, org: OrganizationDto): string {
		return org.id;
	}

	trackByProductOption(
		index: number,
		option: { value: string; viewValue: string },
	): string {
		return option.value;
	}

	trackBySubsidiary(index: number, subsidiary: OrganizationDto): string {
		return subsidiary.id;
	}

	updateBrokerStats(): void {
		let orgCount = 0;
		let livesCount = 0;
		this.filteredOrganizations.forEach((org) => {
			orgCount++;
			livesCount += org._count?.totalBeneficiaries || 0;
		});
		this.brokerStats = { lives: livesCount, organizations: orgCount };
	}

	private addFilters(
		andConditions: any[],
		productFilterValue: any,
		brokerFilterValue: any,
	): void {
		if (productFilterValue && productFilterValue !== 'all') {
			andConditions.push({
				products: {
					some: {
						deletedAt: null,
						products: { id: productFilterValue, deletedAt: null },
					},
				},
			});
		}

		if (brokerFilterValue && brokerFilterValue.length > 0) {
			const hasNone = brokerFilterValue.includes('none');
			const brokerIds = brokerFilterValue.filter((id: string) => id !== 'none');
			const brokerOrConditions = [];

			if (hasNone) {
				brokerOrConditions.push({
					organizationBrokerLink: null,
				});
			}

			if (brokerIds.length > 0) {
				brokerOrConditions.push({
					organizationBrokerLink: {
						deletedAt: null,
						partnerBrokerId: { in: brokerIds },
					},
				});
			}

			if (brokerOrConditions.length > 0) {
				andConditions.push({ OR: brokerOrConditions });
			}
		}
	}

	private buildMainSearchConditions(
		term: string,
		productFilterValue: any,
		brokerFilterValue: any,
	): any[] {
		const andConditions: any[] = [
			{
				active: true,
			},
		];

		andConditions.push({
			OR: [
				{ name_secondary: { contains: term, mode: 'insensitive' } },
				{ name: { contains: term, mode: 'insensitive' } },
				{ cnpj: { contains: term, mode: 'insensitive' } },
				{ name_economic_group: { contains: term, mode: 'insensitive' } },
			],
		});

		this.addFilters(andConditions, productFilterValue, brokerFilterValue);

		return andConditions;
	}

	private buildMatrizFromSubsidiaryConditions(
		matrizIds: string[],
		productFilterValue: any,
		brokerFilterValue: any,
	): any[] {
		const andConditions: any[] = [
			{
				id: { in: matrizIds },
				active: true,
			},
		];

		this.addFilters(andConditions, productFilterValue, brokerFilterValue);

		return andConditions;
	}

	private buildSubsidiarySearchConditions(term: string): any[] {
		const andConditions: any[] = [
			{
				active: true,
				is_matriz: false,
				matriz_id: { not: null },
			},
		];

		andConditions.push({
			OR: [
				{ name_secondary: { contains: term, mode: 'insensitive' } },
				{ name: { contains: term, mode: 'insensitive' } },
				{ cnpj: { contains: term, mode: 'insensitive' } },
				{ name_economic_group: { contains: term, mode: 'insensitive' } },
			],
		});

		return andConditions;
	}

	private getOrganizationSelectQuery(): string {
		return JSON.stringify({
			id: true,
			_count: {
				select: {
					beneficiaries: { where: { active: true } },
					users: { where: { active: true } },
				},
			},
			address: { select: { state: true } },
			cnpj: true,
			hasDigitalHospital: true,
			is_matriz: true,
			isFavorite: true,
			name_economic_group: true,
			name_secondary: true,
			organizationBrokerLink: {
				select: {
					partnerBroker: { select: { id: true, name: true } },
				},
				where: { deletedAt: null },
			},
			products: {
				select: { products: { select: { id: true, name: true } } },
				where: { deletedAt: null, products: { deletedAt: null } },
			},
			subsidiaries: {
				select: {
					id: true,
					_count: {
						select: {
							beneficiaries: { where: { active: true } },
							users: { where: { active: true } },
						},
					},
					address: { select: { state: true } },
					cnpj: true,
					is_matriz: true,
					isFavorite: true,
					name_economic_group: true,
					name_secondary: true,
				},
			},
		});
	}

	private initializeFromQueryParams(): void {
		const queryParams = this.route.snapshot.queryParams;
		const formValues: any = {};

		if (queryParams.term) {
			formValues.term = queryParams.term;
		}

		if (queryParams.produto) {
			const productExists = this.productFilterOptions.some(
				(option) => option.value === queryParams.produto,
			);

			if (productExists) {
				formValues.productFilter = queryParams.produto;
			}
		}

		if (queryParams.corretora) {
			const brokerValues = Array.isArray(queryParams.corretora)
				? queryParams.corretora
				: [queryParams.corretora];
			const validBrokers = brokerValues.filter(
				(brokerId) =>
					brokerId === 'none' ||
					this.partnerBrokers.some((broker) => broker.id === brokerId),
			);

			if (validBrokers.length > 0) {
				formValues.brokerFilter = validBrokers;
			}
		}

		if (queryParams.pagina) {
			const page = parseInt(queryParams.pagina, 10);

			if (!isNaN(page) && page > 0) {
				this.currentPage = page;
			}
		}

		if (Object.keys(formValues).length > 0) {
			this.searchForm.patchValue(formValues);
		}

		this.searchOrganizations();
	}

	private searchWithSubsidiarySupport(term: string): void {
		const select = this.getOrganizationSelectQuery();

		const subsidiarySelect = JSON.stringify({
			id: true,
			cnpj: true,
			matriz_id: true,
			name: true,
			name_economic_group: true,
			name_secondary: true,
		});

		const productFilterValue = this.searchForm.get('productFilter')?.value;
		const brokerFilterValue = this.searchForm.get('brokerFilter')?.value;

		const mainConditions = this.buildMainSearchConditions(
			term,
			productFilterValue,
			brokerFilterValue,
		);
		const mainWhere = JSON.stringify({ AND: mainConditions });

		const subsidiaryConditions = this.buildSubsidiarySearchConditions(term);
		const subsidiaryWhere = JSON.stringify({ AND: subsidiaryConditions });

		const orderBy = JSON.stringify({
			name_economic_group: SortingType.ASCENDING,
		});
		const subsidiarySearch$ = this.organizationsService.findMany(
			new PaginationArgs({
				limit: 50,
				orderBy,
				page: 1,
				select: subsidiarySelect,
				where: subsidiaryWhere,
			}),
		);

		this.searchSubscription$ = subsidiarySearch$.subscribe(
			(subsidiaryResponse) => {
				const matrizIds = subsidiaryResponse.result
					.filter((sub: any) => sub.matriz_id)
					.map((sub: any) => sub.matriz_id);

				const queries = [
					this.organizationsService.findMany(
						new PaginationArgs({
							limit: this.limit,
							orderBy,
							page: this.currentPage,
							select,
							where: mainWhere,
						}),
					),
				];

				if (matrizIds.length > 0) {
					const matrizConditions = this.buildMatrizFromSubsidiaryConditions(
						matrizIds,
						productFilterValue,
						brokerFilterValue,
					);
					const matrizWhere = JSON.stringify({ AND: matrizConditions });

					queries.push(
						this.organizationsService.findMany(
							new PaginationArgs({
								limit: this.limit,
								orderBy,
								page: this.currentPage,
								select,
								where: matrizWhere,
							}),
						),
					);
				}

				forkJoin(queries).subscribe(
					(responses) => {
						const allOrganizations: OrganizationDto[] = [];

						responses.forEach((response) => {
							response.result.forEach((org: OrganizationDto) => {
								if (
									!allOrganizations.find((existing) => existing.id === org.id)
								) {
									allOrganizations.push(org);
								}
							});
						});

						this.organizations = allOrganizations.map((org) => {
							const orgWithBroker = { ...org };

							orgWithBroker.partnerBroker =
								org.organizationBrokerLink?.partnerBroker || null;

							if (orgWithBroker.subsidiaries) {
								orgWithBroker.subsidiaries = (
									orgWithBroker.subsidiaries as OrganizationDto[]
								).map((sub) => {
									const subWithBroker = { ...sub };
									subWithBroker.partnerBroker =
										sub.organizationsPartners &&
										sub.organizationsPartners.length > 0
											? sub.organizationsPartners[0].partners
											: null;

									return subWithBroker;
								});
							}

							if (orgWithBroker.products && orgWithBroker.products.length > 1) {
								orgWithBroker.products.sort((a, b) =>
									a.products.name.localeCompare(b.products.name),
								);
							}

							return orgWithBroker;
						});

						this.filteredOrganizations = this.sortOrganizations(
							this.organizations,
						);

						this.calculateTotal();
						this.updateBrokerStats();
						this.count = responses.reduce(
							(max, resp) => Math.max(max, resp.count),
							0,
						);
						this.isLoading = false;
					},
					() => (this.isLoading = false),
				);
			},
			() => (this.isLoading = false),
		);
	}

	private searchWithoutTerm(): void {
		const select = this.getOrganizationSelectQuery();

		const productFilterValue = this.searchForm.get('productFilter')?.value;
		const brokerFilterValue = this.searchForm.get('brokerFilter')?.value;

		const andConditions: any[] = [
			{
				active: true,
			},
		];

		if (productFilterValue && productFilterValue !== 'all') {
			andConditions.push({
				products: {
					some: {
						deletedAt: null,
						products: { id: productFilterValue, deletedAt: null },
					},
				},
			});
		}

		if (brokerFilterValue && brokerFilterValue.length > 0) {
			const hasNone = brokerFilterValue.includes('none');
			const brokerIds = brokerFilterValue.filter((id: string) => id !== 'none');
			const brokerOrConditions = [];

			if (hasNone) {
				brokerOrConditions.push({
					organizationBrokerLink: null,
				});
			}

			if (brokerIds.length > 0) {
				brokerOrConditions.push({
					organizationBrokerLink: {
						deletedAt: null,
						partnerBrokerId: { in: brokerIds },
					},
				});
			}

			if (brokerOrConditions.length > 0) {
				andConditions.push({ OR: brokerOrConditions });
			}
		}

		const where: string = JSON.stringify({ AND: andConditions });
		const orderBy = JSON.stringify({
			name_economic_group: SortingType.ASCENDING,
		});

		this.searchSubscription$ = this.organizationsService
			.findMany(
				new PaginationArgs({
					limit: this.limit,
					orderBy,
					page: this.currentPage,
					select,
					where,
				}),
			)
			.subscribe(
				(response: Pagination<OrganizationDto>) => {
					this.organizations = response.result.map((org) => {
						const orgWithBroker = { ...org };

						orgWithBroker.partnerBroker =
							org.organizationBrokerLink?.partnerBroker || null;

						if (orgWithBroker.subsidiaries) {
							orgWithBroker.subsidiaries = (
								orgWithBroker.subsidiaries as OrganizationDto[]
							).map((sub) => {
								const subWithBroker = { ...sub };
								subWithBroker.partnerBroker =
									sub.organizationsPartners &&
									sub.organizationsPartners.length > 0
										? sub.organizationsPartners[0].partners
										: null;

								return subWithBroker;
							});
						}

						if (orgWithBroker.products && orgWithBroker.products.length > 1) {
							orgWithBroker.products.sort((a, b) =>
								a.products.name.localeCompare(b.products.name),
							);
						}

						return orgWithBroker;
					});

					this.filteredOrganizations = this.sortOrganizations(
						this.organizations,
					);

					this.calculateTotal();
					this.updateBrokerStats();
					this.count = response.count;
					this.isLoading = false;
				},
				() => (this.isLoading = false),
			);
	}

	private sortOrganizations(orgs: OrganizationDto[]): OrganizationDto[] {
		if (!orgs) {
			return [];
		}

		orgs.sort((a, b) => {
			const aHasFavorite =
				a.isFavorite || (a.subsidiaries || []).some((sub) => sub.isFavorite);

			const bHasFavorite =
				b.isFavorite || (b.subsidiaries || []).some((sub) => sub.isFavorite);

			if (aHasFavorite && !bHasFavorite) {
				return -1;
			}

			if (!aHasFavorite && bHasFavorite) {
				return 1;
			}

			const nameA = a.name_economic_group || a.name_secondary || '';
			const nameB = b.name_economic_group || b.name_secondary || '';

			return nameA.localeCompare(nameB);
		});

		orgs.forEach((org) => {
			if (org.subsidiaries && org.subsidiaries.length > 0) {
				(org.subsidiaries as OrganizationDto[]).sort((a, b) => {
					if (a.isFavorite && !b.isFavorite) {
						return -1;
					}

					if (!a.isFavorite && b.isFavorite) {
						return 1;
					}

					const nameA = a.name_secondary || '';
					const nameB = b.name_secondary || '';

					return nameA.localeCompare(nameB);
				});
			}
		});

		return orgs;
	}

	private updateQueryParams(): void {
		const queryParams: any = {};

		const term = this.searchForm.get('term')?.value;

		if (term?.trim()) {
			queryParams.term = term.trim();
		}

		const productFilter = this.searchForm.get('productFilter')?.value;

		if (productFilter && productFilter !== 'all') {
			queryParams.produto = productFilter;
		}

		const brokerFilter = this.searchForm.get('brokerFilter')?.value;

		if (brokerFilter && brokerFilter.length > 0) {
			queryParams.corretora = brokerFilter;
		}

		if (this.currentPage > 1) {
			queryParams.pagina = this.currentPage.toString();
		}

		this.router.navigate([], {
			queryParams,
			queryParamsHandling: 'replace',
			relativeTo: this.route,
		});
	}
}
